import type { ComponentProps, ReactNode } from 'react';
import { Link, type To } from 'react-router';

/**
 * 표 행의 대표 셀(보통 이름)을 진짜 링크로 만든다 — admin-ds DataTable의 onRowClick은 <tr> 클릭만 받아서
 * Tab으로 행에 들어갈 수 없고 ⌘/Ctrl·가운데 클릭으로 새 탭에 열 수도 없다.
 * 행 클릭(onRowClick)은 마우스 편의로 남겨 두고, 이 링크 클릭은 행으로 전파하지 않아 두 번 이동하지 않게 한다.
 */
type RowLinkProps = Omit<ComponentProps<typeof Link>, 'to' | 'children'> & {
  to: To;
  children: ReactNode;
  /**
   * 링크의 클릭 영역을 행 전체로 넓힌다(stretched link). 행 어디에 올려도 같은 링크라서 브라우저가 주소를 보여 주고,
   * ⌘/Ctrl·가운데 클릭과 우클릭 "새 탭에서 열기"가 행 전체에서 된다.
   * 쓰는 쪽이 행(<tr>)에 `relative`를 줘야 한다(없으면 표 전체로 퍼진다). 행 안의 버튼·복사할 글자는
   * `relative z-10`으로 링크 위에 올려야 눌리고 선택된다. 링크 영역의 글자는 끌어서 선택할 수 없다.
   */
  stretched?: boolean;
};

const BASE =
  'text-foreground focus-visible:ring-ring rounded-sm underline-offset-4 outline-none hover:underline focus-visible:ring-2';

export function RowLink({ to, children, stretched = false, ...linkProps }: RowLinkProps) {
  return (
    <Link
      to={to}
      {...linkProps}
      onClick={(event) => {
        event.stopPropagation();
      }}
      className={stretched ? `${BASE} after:absolute after:inset-0` : BASE}
    >
      {children}
    </Link>
  );
}
